import { Component } from "react";
import NewDashboardLayout from "../components/NewDashboardlayout";
import Swal from "sweetalert2";
import { Card, Button, Badge, Spinner, Form, Modal } from "react-bootstrap";

const baseUrl = process.env.REACT_APP_BASE_URL;

class LeadScientistAssignments extends Component {
  constructor(props) {
    super(props);
    this.state = {
      assignments: [],
      isLoading: true,
      processingId: null,
      showDeclineModal: false,
      selectedAssignment: null,
      declineReason: "",
    };
  }

  componentDidMount() {
    this.fetchPendingAssignments();
  }

  fetchPendingAssignments = async () => {
    this.setState({ isLoading: true });

    try {
      const response = await fetch(
        `${baseUrl}/v2/user/lead-scientist-assignments/pending`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );

      if (response.status === 401) {
        Swal.fire({
          title: "Session Expired",
          text: "Session expired. Please login",
          icon: "error",
          confirmButtonText: "OK",
        }).then(() => {
          this.props.history.push("/login");
        });
        return;
      }

      const data = await response.json();
      this.setState({ assignments: data, isLoading: false });
    } catch (error) {
      console.error("Error fetching assignments:", error);
      this.setState({ isLoading: false });
      Swal.fire({
        title: "Error",
        text: "Failed to load assignment requests",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  acceptAssignment = async (assignmentId) => {
    this.setState({ processingId: assignmentId });

    try {
      const response = await fetch(
        `${baseUrl}/v2/user/lead-scientist-assignments/${assignmentId}/accept`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to accept assignment");
      }

      Swal.fire({
        title: "Success!",
        text: "You have accepted the assignment. The company has been notified.",
        icon: "success",
        confirmButtonText: "OK",
      });

      // Refresh the list
      this.fetchPendingAssignments();
    } catch (error) {
      console.error("Error accepting assignment:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to accept assignment",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      this.setState({ processingId: null });
    }
  };

  openDeclineModal = (assignment) => {
    this.setState({
      showDeclineModal: true,
      selectedAssignment: assignment,
      declineReason: "",
    });
  };

  closeDeclineModal = () => {
    this.setState({
      showDeclineModal: false,
      selectedAssignment: null,
      declineReason: "",
    });
  };

  declineAssignment = async () => {
    const { selectedAssignment, declineReason } = this.state;
    if (!selectedAssignment) return;

    this.setState({ processingId: selectedAssignment.assignmentId });
    this.closeDeclineModal();

    try {
      const response = await fetch(
        `${baseUrl}/v2/user/lead-scientist-assignments/${selectedAssignment.assignmentId}/decline`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({ reason: declineReason || null }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to decline assignment");
      }

      Swal.fire({
        title: "Declined",
        text: "You have declined the assignment. The company has been notified.",
        icon: "info",
        confirmButtonText: "OK",
      });

      // Refresh the list
      this.fetchPendingAssignments();
    } catch (error) {
      console.error("Error declining assignment:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to decline assignment",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      this.setState({ processingId: null });
    }
  };

  formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  render() {
    const {
      assignments,
      isLoading,
      processingId,
      showDeclineModal,
      selectedAssignment,
      declineReason,
    } = this.state;

    return (
      <NewDashboardLayout>
        <div className="container-fluid p-4">
          <h2 className="mb-4">Lead Food Scientist Assignment Requests</h2>
          <p className="text-muted mb-4">
            Companies have requested you to serve as their lead food scientist.
            Please review and respond to each request.
          </p>

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading assignment requests...</p>
            </div>
          ) : assignments.length === 0 ? (
            <Card className="text-center py-5">
              <Card.Body>
                <i
                  className="material-icons"
                  style={{ fontSize: "48px", color: "#6c757d" }}
                >
                  inbox
                </i>
                <h5 className="mt-3">No Pending Requests</h5>
                <p className="text-muted">
                  You don't have any pending assignment requests at this time.
                </p>
              </Card.Body>
            </Card>
          ) : (
            <div className="row">
              {assignments.map((assignment) => (
                <div className="col-md-6 col-lg-4 mb-4" key={assignment.assignmentId}>
                  <Card className="h-100 shadow-sm">
                    <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                      <span>
                        <i className="material-icons align-middle me-2">business</i>
                        Assignment Request
                      </span>
                      <Badge bg="warning" text="dark">
                        Pending
                      </Badge>
                    </Card.Header>
                    <Card.Body>
                      <h5 className="card-title">{assignment.companyName}</h5>

                      {/* Company Address */}
                      <p className="card-text text-muted mb-2">
                        <i className="material-icons align-middle me-1" style={{ fontSize: "16px" }}>
                          location_on
                        </i>
                        {assignment.companyAddress || "Address not provided"}
                      </p>

                      <hr />

                      {/* Contact Person Section */}
                      <div className="mb-3 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <small className="text-muted d-block mb-2" style={{ fontWeight: '600' }}>
                          <i className="material-icons align-middle me-1" style={{ fontSize: "14px" }}>
                            person
                          </i>
                          Company Contact
                        </small>
                        <p className="mb-1" style={{ fontWeight: '500' }}>{assignment.requestedByName}</p>
                        {assignment.requestedByDesignation && (
                          <p className="mb-1 text-muted" style={{ fontSize: '14px' }}>
                            <i className="material-icons align-middle me-1" style={{ fontSize: "14px" }}>
                              badge
                            </i>
                            {assignment.requestedByDesignation}
                          </p>
                        )}
                        <p className="mb-1 text-muted" style={{ fontSize: '14px' }}>
                          <i className="material-icons align-middle me-1" style={{ fontSize: "14px" }}>
                            email
                          </i>
                          {assignment.requestedByEmail}
                        </p>
                        {assignment.requestedByPhone && (
                          <p className="mb-0 text-muted" style={{ fontSize: '14px' }}>
                            <i className="material-icons align-middle me-1" style={{ fontSize: "14px" }}>
                              phone
                            </i>
                            {assignment.requestedByPhone}
                          </p>
                        )}
                      </div>

                      <div className="mb-2">
                        <small className="text-muted">Request Date:</small>
                        <p className="mb-0">{this.formatDate(assignment.createdAt)}</p>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted">Expires:</small>
                        <p className="mb-0 text-danger">
                          {this.formatDate(assignment.expiresAt)}
                        </p>
                      </div>
                    </Card.Body>
                    <Card.Footer className="bg-white">
                      <div className="d-flex gap-2">
                        <Button
                          variant="success"
                          className="flex-grow-1"
                          onClick={() => this.acceptAssignment(assignment.assignmentId)}
                          disabled={processingId === assignment.assignmentId}
                        >
                          {processingId === assignment.assignmentId ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <>
                              <i className="material-icons align-middle me-1" style={{ fontSize: "18px" }}>
                                check
                              </i>
                              Accept
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline-danger"
                          className="flex-grow-1"
                          onClick={() => this.openDeclineModal(assignment)}
                          disabled={processingId === assignment.assignmentId}
                        >
                          <i className="material-icons align-middle me-1" style={{ fontSize: "18px" }}>
                            close
                          </i>
                          Decline
                        </Button>
                      </div>
                    </Card.Footer>
                  </Card>
                </div>
              ))}
            </div>
          )}

          {/* Decline Reason Modal */}
          <Modal show={showDeclineModal} onHide={this.closeDeclineModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>Decline Assignment Request</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                Are you sure you want to decline the assignment request from{" "}
                <strong>{selectedAssignment?.companyName}</strong>?
              </p>
              <Form.Group>
                <Form.Label>Reason for declining (optional):</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter reason..."
                  value={declineReason}
                  onChange={(e) => this.setState({ declineReason: e.target.value })}
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  Maximum 500 characters. The company will be notified of your decision.
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={this.closeDeclineModal}>
                Cancel
              </Button>
              <Button variant="danger" onClick={this.declineAssignment}>
                Decline Request
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </NewDashboardLayout>
    );
  }
}

export default LeadScientistAssignments;
